import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'periods' | 'recurring';

/**
 * FR-ACC-07 + FR-ACC-10 + FR-ACC-02.05 — Period close, year-end, recurring JEs.
 * JE reversal lives in JournalEntriesPage; this is the month-end admin page.
 */
export function PeriodClosePage() {
  const [tab, setTab] = useState<Tab>('periods');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'periods',   label: 'Period close' },
    { key: 'recurring', label: 'Recurring JEs' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Controls"
        title="Period close"
        subtitle="Close months in sequence, lock posting, and schedule recurring journal entries."
      />

      {/* Pill tab strip */}
      <div className="card p-1.5 mb-6 inline-flex gap-1 border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 text-sm font-semibold rounded-pill transition-all',
              tab === t.key
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'periods' && <PeriodsTab />}
      {tab === 'recurring' && <RecurringTab />}
    </div>
  );
}

// ============================================================
// PeriodsTab — the month-close stepper
// ============================================================
function PeriodsTab() {
  const qc = useQueryClient();
  const thisYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState(thisYear);
  const [closingPeriod, setClosingPeriod] = useState<string | null>(null);
  const [reopeningPeriod, setReopeningPeriod] = useState<string | null>(null);
  const [showYearEnd, setShowYearEnd] = useState(false);

  const listQuery = useQuery({
    queryKey: ['periods', fiscalYear],
    queryFn: () => accountingApi.listPeriods(fiscalYear),
  });

  const closeMut = useMutation({
    mutationFn: ({ period, notes }: any) => accountingApi.closePeriod(period, notes),
    onSuccess: () => {
      toast.success('Period closed');
      qc.invalidateQueries({ queryKey: ['periods'] });
      setClosingPeriod(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reopenMut = useMutation({
    mutationFn: ({ period, reason }: any) => accountingApi.reopenPeriod(period, reason),
    onSuccess: () => {
      toast.success('Period reopened');
      qc.invalidateQueries({ queryKey: ['periods'] });
      setReopeningPeriod(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const yearEndMut = useMutation({
    mutationFn: () => accountingApi.closeYearEnd({ fiscalYear }),
    onSuccess: (d) => {
      toast.success(
        `Fiscal ${d.fiscalYear} closed · Net income ${fmtCurrency(d.netIncome)} · JE ${d.closingJeNumber}`,
        { duration: 8000 }
      );
      qc.invalidateQueries({ queryKey: ['periods'] });
      setShowYearEnd(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const periodsByMonth = useMemo(() => {
    const m = new Map<string, any>();
    for (const p of (listQuery.data ?? [])) m.set(p.period, p);
    return m;
  }, [listQuery.data]);

  const monthKeys = useMemo(
    () => [...Array(12).keys()].map((i) => `${fiscalYear}-${String(i + 1).padStart(2, '0')}`),
    [fiscalYear]
  );

  const { closedCount, openCount, reopenedCount, totalJe, allClosed } = useMemo(() => {
    let closed = 0, open = 0, reopened = 0, je = 0;
    for (const key of monthKeys) {
      const p = periodsByMonth.get(key);
      const s = p?.status ?? 'open';
      if (s === 'closed') closed++;
      else if (s === 'reopened') reopened++;
      else open++;
      je += p?.je_count ?? 0;
    }
    return { closedCount: closed, openCount: open, reopenedCount: reopened, totalJe: je, allClosed: closed === 12 };
  }, [monthKeys, periodsByMonth]);

  // The "next period to close" — earliest non-closed month
  const nextCloseTarget = monthKeys.find(
    (key) => periodsByMonth.get(key)?.status !== 'closed'
  );

  return (
    <div>
      {/* Fiscal year selector + year-end CTA */}
      <div className="card p-4 mb-6 flex gap-4 items-end flex-wrap border-slate-200">
        <div>
          <label className="label">Fiscal year</label>
          <input
            type="number"
            className="input w-32 tabular-nums"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(parseInt(e.target.value) || thisYear)}
          />
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          {!allClosed && (
            <span className="text-xs text-slate-500 max-w-xs text-right">
              All 12 months must be closed before year-end close.
            </span>
          )}
          <button
            className={clsx(
              'btn-primary',
              !allClosed && 'opacity-50 cursor-not-allowed'
            )}
            disabled={!allClosed || yearEndMut.isPending}
            onClick={() => setShowYearEnd(true)}
          >
            Year-end close {fiscalYear}
          </button>
        </div>
      </div>

      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : (
        <>
          {/* Progress strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <KpiTile label="Months closed" value={`${closedCount} / 12`}
              accent={closedCount === 12 ? 'sage' : 'finance'}
              sublabel={`${((closedCount / 12) * 100).toFixed(0)}% complete`} />
            <KpiTile label="Open" value={openCount}
              accent={openCount > 0 ? 'amber' : 'default'} />
            <KpiTile label="Reopened" value={reopenedCount}
              accent={reopenedCount > 0 ? 'amber' : 'default'} />
            <KpiTile label="Journal entries (YTD)" value={totalJe} />
          </div>

          {/* Year progress bar */}
          <div className="card p-5 mb-6 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow text-slate-500">Year progress</div>
                <div className="font-display font-bold text-slate-900 mt-0.5">FY {fiscalYear}</div>
              </div>
              {nextCloseTarget && !allClosed && (
                <div className="text-right">
                  <div className="eyebrow text-slate-500">Next to close</div>
                  <div className="font-display font-semibold text-finance-700 mt-0.5">
                    {new Date(fiscalYear, parseInt(nextCloseTarget.slice(-2)) - 1).toLocaleDateString('en', { month: 'long' })}
                  </div>
                </div>
              )}
              {allClosed && (
                <div className="text-right">
                  <div className="eyebrow text-sage-700">Ready</div>
                  <div className="font-display font-semibold text-sage-700 mt-0.5">Year-end close available</div>
                </div>
              )}
            </div>

            {/* Sequential bar — 12 cells, one per month, with status color */}
            <div className="flex h-3 rounded-sm overflow-hidden gap-0.5 bg-slate-100">
              {monthKeys.map((key) => {
                const p = periodsByMonth.get(key);
                const s = p?.status ?? 'open';
                const tone =
                  s === 'closed'   ? 'bg-sage-500' :
                  s === 'reopened' ? 'bg-amber-500' :
                  s === 'closing'  ? 'bg-finance-400' :
                                     'bg-slate-200';
                return (
                  <div
                    key={key}
                    className={clsx('flex-1', tone)}
                    title={`${new Date(fiscalYear, parseInt(key.slice(-2)) - 1).toLocaleDateString('en', { month: 'long' })}: ${s}`}
                  />
                );
              })}
            </div>
            <div className="flex items-center gap-4 text-2xs text-slate-600 mt-3">
              <LegendDot color="bg-sage-500" label="Closed" />
              <LegendDot color="bg-amber-500" label="Reopened" />
              <LegendDot color="bg-slate-200" label="Open" />
            </div>
          </div>

          {/* Month cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {monthKeys.map((key) => {
              const p = periodsByMonth.get(key);
              const status = p?.status ?? 'open';
              const monthIdx = parseInt(key.slice(-2)) - 1;
              const monthName = new Date(fiscalYear, monthIdx).toLocaleDateString('en', { month: 'long' });
              const monthShort = new Date(fiscalYear, monthIdx).toLocaleDateString('en', { month: 'short' });
              return (
                <MonthCard
                  key={key}
                  periodKey={key}
                  monthName={monthName}
                  monthShort={monthShort}
                  status={status}
                  period={p}
                  onClose={() => setClosingPeriod(key)}
                  onReopen={() => setReopeningPeriod(key)}
                />
              );
            })}
          </div>
        </>
      )}

      {closingPeriod && (
        <CloseDialog
          periodKey={closingPeriod}
          loading={closeMut.isPending}
          onClose={() => setClosingPeriod(null)}
          onConfirm={(notes) => closeMut.mutate({ period: closingPeriod, notes })}
        />
      )}

      {reopeningPeriod && (
        <ReopenDialog
          periodKey={reopeningPeriod}
          loading={reopenMut.isPending}
          onClose={() => setReopeningPeriod(null)}
          onConfirm={(reason) => reopenMut.mutate({ period: reopeningPeriod, reason })}
        />
      )}

      {showYearEnd && (
        <YearEndDialog
          fiscalYear={fiscalYear}
          loading={yearEndMut.isPending}
          onClose={() => setShowYearEnd(false)}
          onConfirm={() => yearEndMut.mutate()}
        />
      )}
    </div>
  );
}

// ============================================================
// MonthCard — one per month, gets state-appropriate CTA
// ============================================================
function MonthCard({
  periodKey, monthName, monthShort, status, period, onClose, onReopen,
}: {
  periodKey: string;
  monthName: string;
  monthShort: string;
  status: string;
  period?: any;
  onClose: () => void;
  onReopen: () => void;
}) {
  const tone =
    status === 'closed'   ? 'border-sage-200 bg-sage-50/30' :
    status === 'reopened' ? 'border-amber-200 bg-amber-50/30' :
    status === 'closing'  ? 'border-finance-200 bg-finance-50/30' :
                            'border-slate-200 bg-white';

  return (
    <div className={clsx('card p-4 relative overflow-hidden', tone)}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="eyebrow text-slate-500">{monthShort}</div>
          <div className="font-display font-bold text-slate-900 mt-0.5">{monthName}</div>
          <div className="font-mono text-2xs text-slate-400 mt-0.5">{periodKey}</div>
        </div>
        <PeriodStatusBadge status={status} />
      </div>

      {period?.je_count != null && (
        <div className="text-xs text-slate-600 mb-2 font-mono tabular-nums">
          {period.je_count} journal {period.je_count === 1 ? 'entry' : 'entries'}
        </div>
      )}

      {period?.closed_at && (
        <div className="text-2xs text-slate-500 mb-1">
          Closed {new Date(period.closed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          {period.closed_by_name && <> · {period.closed_by_name}</>}
        </div>
      )}
      {period?.reopened_at && (
        <div className="text-2xs text-amber-700 mb-1 italic">
          Reopened: {period.reopen_reason}
        </div>
      )}

      {(status === 'open' || status === 'reopened') && (
        <button
          className="mt-3 w-full text-xs font-semibold bg-white text-finance-700 ring-1 ring-inset ring-finance-200 hover:bg-finance-50 rounded-pill px-3 py-1.5 transition-colors"
          onClick={onClose}
        >
          Close period
        </button>
      )}

      {status === 'closed' && (
        <button
          className="mt-3 w-full text-2xs font-semibold text-amber-700 hover:bg-amber-100 rounded-pill px-3 py-1.5 transition-colors"
          onClick={onReopen}
        >
          Reopen (super-admin)
        </button>
      )}
    </div>
  );
}

function PeriodStatusBadge({ status }: { status: string }) {
  const cls = {
    open:     'bg-slate-100 text-slate-700 ring-slate-200',
    closing:  'bg-finance-50 text-finance-800 ring-finance-200',
    closed:   'bg-sage-50 text-sage-700 ring-sage-200',
    reopened: 'bg-amber-50 text-amber-800 ring-amber-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status}
    </span>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={clsx('w-2 h-2 rounded-sm', color)} />
      <span className="uppercase tracking-wider font-semibold">{label}</span>
    </div>
  );
}

// ============================================================
// Dialogs — replace prompt()/confirm()
// ============================================================
function CloseDialog({
  periodKey, loading, onClose, onConfirm,
}: {
  periodKey: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => void;
}) {
  const [notes, setNotes] = useState('');
  const [year, month] = periodKey.split('-');
  const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en', { month: 'long', year: 'numeric' });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-finance-700 mb-1">Close · Period</div>
        <h2 className="text-xl font-bold font-display text-slate-900">
          Close {monthName}
        </h2>
        <p className="text-sm text-slate-500 mt-2">
          Locks this period against further posting. Journal entries with dates in this range will be rejected unless the period is reopened by a super-admin.
        </p>
      </div>

      <div className="px-6 py-5">
        <label className="label">Closing notes <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span></label>
        <textarea
          className="input"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Month-end accruals reviewed by J. Smith"
        />
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary"
          disabled={loading}
          onClick={() => onConfirm(notes || undefined)}
        >
          {loading ? 'Closing…' : 'Close period'}
        </button>
      </div>
    </Modal>
  );
}

function ReopenDialog({
  periodKey, loading, onClose, onConfirm,
}: {
  periodKey: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [year, month] = periodKey.split('-');
  const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en', { month: 'long', year: 'numeric' });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-amber-700 mb-1">Reopen · Period</div>
        <h2 className="text-xl font-bold font-display text-slate-900">
          Reopen {monthName}
        </h2>
        <p className="text-sm text-slate-500 mt-2">
          Super-admin action. The period becomes post-enabled again. A reason is logged on the audit trail.
        </p>
      </div>

      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Audit adjustment requested by Ernst & Young"
        />
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-amber-600 hover:!bg-amber-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason)}
        >
          {loading ? 'Reopening…' : 'Reopen period'}
        </button>
      </div>
    </Modal>
  );
}

function YearEndDialog({
  fiscalYear, loading, onClose, onConfirm,
}: {
  fiscalYear: number;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const expected = `CLOSE FY${fiscalYear}`;
  const canSubmit = typed === expected;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Year-end · Irreversible</div>
        <h2 className="text-xl font-bold font-display text-slate-900">
          Close fiscal year {fiscalYear}
        </h2>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
          This posts a closing journal entry that <strong className="text-slate-900">zeroes out all Revenue, Expense, and COGS accounts</strong> and transfers the period's net income into Retained Earnings.
        </p>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          <strong className="text-danger">This cannot be undone.</strong> Subsequent entries dated in fiscal {fiscalYear} will be rejected. Only reopen individual periods first if you need to backdate.
        </p>
      </div>

      <div className="px-6 py-5">
        <div className="bg-danger/5 border border-danger/30 rounded-xl p-4">
          <label className="label text-slate-800">
            Type <span className="font-mono font-bold text-danger">{expected}</span> to confirm
          </label>
          <input
            className="input font-mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={expected}
            autoFocus
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!canSubmit || loading}
          onClick={onConfirm}
        >
          {loading ? 'Closing year…' : 'Close fiscal year'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// RecurringTab — preserved, lightly restyled
// ============================================================
function RecurringTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['recurring-templates'],
    queryFn: () => accountingApi.listRecurringTemplates(),
  });

  const kpis = useMemo(() => {
    const rows = listQuery.data ?? [];
    const active = rows.filter((t: any) => t.status === 'active').length;
    const paused = rows.filter((t: any) => t.status === 'paused').length;
    const totalRuns = rows.reduce((s: number, t: any) => s + (t.run_count ?? 0), 0);
    return { total: rows.length, active, paused, totalRuns };
  }, [listQuery.data]);

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Templates" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent="sage" />
        <KpiTile label="Paused" value={kpis.paused}
          accent={kpis.paused > 0 ? 'amber' : 'default'} />
        <KpiTile label="Total runs" value={kpis.totalRuns} accent="finance" />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New template</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        <div className="card overflow-hidden border-slate-200">
          {listQuery.isLoading ? <LoadingState rows={4} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
            !listQuery.data?.length ? (
              <EmptyState
                message="No recurring templates yet"
                hint="Automate monthly rent, depreciation, allocation JEs, etc."
                action={
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New template</button>
                }
              />
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Name</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Schedule</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Next run</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Runs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(listQuery.data ?? []).map((t: any, i: number) => (
                    <tr key={t.id} onClick={() => setSelected(t.id)}
                      className={clsx(
                        'cursor-pointer transition-colors',
                        selected === t.id
                          ? 'bg-finance-50'
                          : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                      )}>
                      <td className="px-4 py-2 font-medium text-slate-900">{t.name}</td>
                      <td className="px-4 py-2 text-xs text-slate-700 capitalize">{t.schedule}</td>
                      <td className="px-4 py-2">
                        <span className={clsx(
                          'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
                          t.status === 'active' ? 'bg-sage-50 text-sage-700 ring-sage-200' :
                          t.status === 'paused' ? 'bg-amber-50 text-amber-800 ring-amber-200' :
                          'bg-slate-100 text-slate-700 ring-slate-200'
                        )}>{t.status}</span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-600 font-mono tabular-nums">
                        {t.next_run_date ? new Date(t.next_run_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-sm tabular-nums">{t.run_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px] border-slate-200">
          {!selected ? (
            <EmptyState
              message="Select a template"
              hint="Click a row on the left to view its JE lines and run history."
            />
          ) : (
            <TemplateDetail templateId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['recurring-templates'] })} />
          )}
        </div>
      </div>

      {showCreate && <CreateTemplateModal onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['recurring-templates'] });
          setShowCreate(false); setSelected(id);
        }} />}
    </div>
  );
}

function TemplateDetail({ templateId, onChanged }: { templateId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showRun, setShowRun] = useState(false);
  const q = useQuery({
    queryKey: ['recurring-template', templateId],
    queryFn: () => accountingApi.getRecurringTemplate(templateId),
  });

  const runMut = useMutation({
    mutationFn: () => accountingApi.runRecurringTemplate(templateId),
    onSuccess: (d) => {
      toast.success(`JE ${d.jeNumber} posted ${d.runDate} · Next: ${d.nextRunDate ?? 'COMPLETED'}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['recurring-template', templateId] });
      onChanged();
      setShowRun(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pauseMut = useMutation({
    mutationFn: () => accountingApi.pauseRecurringTemplate(templateId),
    onSuccess: () => { toast.success('Paused'); qc.invalidateQueries({ queryKey: ['recurring-template', templateId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const resumeMut = useMutation({
    mutationFn: () => accountingApi.resumeRecurringTemplate(templateId),
    onSuccess: () => { toast.success('Resumed'); qc.invalidateQueries({ queryKey: ['recurring-template', templateId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const t = q.data;
  const tpl = t.template;

  return (
    <div>
      <div className="mb-4">
        <div className="eyebrow text-finance-700 mb-1">Recurring JE</div>
        <div className="font-display font-bold text-lg text-slate-900">{t.name}</div>
        {t.description && <div className="text-sm text-slate-600 mt-1">{t.description}</div>}
      </div>

      <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200 space-y-1.5 text-xs text-slate-700">
        <div><span className="text-slate-400">Schedule:</span> <span className="capitalize">{t.schedule}</span></div>
        <div><span className="text-slate-400">Status:</span> <span className="capitalize">{t.status}</span></div>
        {t.next_run_date && <div><span className="text-slate-400">Next run:</span> <span className="font-mono">{new Date(t.next_run_date).toLocaleDateString()}</span></div>}
        {t.last_run_date && <div><span className="text-slate-400">Last run:</span> <span className="font-mono">{new Date(t.last_run_date).toLocaleDateString()}</span></div>}
        <div><span className="text-slate-400">Runs completed:</span> <span className="font-mono tabular-nums">{t.run_count}</span></div>
      </div>

      <div className="eyebrow text-slate-500 mb-2">Template — {tpl.description}</div>
      <div className="rounded-lg border border-slate-200 overflow-hidden mb-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Account</th>
              <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Debit</th>
              <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tpl.lines.map((l: any, i: number) => (
              <tr key={i}>
                <td className="px-3 py-2 font-mono font-semibold text-slate-900">{l.accountCode}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {l.debit ? <span className="text-slate-900">{fmtCurrency(l.debit)}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {l.credit ? <span className="text-slate-900">{fmtCurrency(l.credit)}</span> : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {t.runs && t.runs.length > 0 && (
        <div className="mb-4">
          <div className="eyebrow text-slate-500 mb-2">Run history</div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Date</th>
                  <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">JE</th>
                  <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {t.runs.map((r: any) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 font-mono tabular-nums text-slate-700">
                      {new Date(r.run_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold text-slate-900">{r.journal_entry_number ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2">
                      {r.status === 'success' ? (
                        <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-sage-50 text-sage-700 ring-sage-200">success</span>
                      ) : r.status === 'failed' ? (
                        <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200" title={r.error_message}>failed</span>
                      ) : (
                        <span className="text-slate-500 capitalize text-xs">{r.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        {t.status === 'active' && (
          <>
            <button className="btn-secondary" disabled={pauseMut.isPending} onClick={() => pauseMut.mutate()}>Pause</button>
            <button className="btn-primary" disabled={runMut.isPending}
              onClick={() => setShowRun(true)}>
              {runMut.isPending ? 'Running…' : 'Run now'}
            </button>
          </>
        )}
        {t.status === 'paused' && (
          <button className="btn-primary" disabled={resumeMut.isPending} onClick={() => resumeMut.mutate()}>Resume</button>
        )}
      </div>

      {showRun && (
        <Modal
          onClose={() => setShowRun(false)}
          overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow mb-1">Accounting · Recurring</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Run template now?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              A journal entry will be posted dated{' '}
              <span className="font-semibold text-ink-900">{t.next_run_date ?? 'today'}</span>.
              The next run date will advance to the following period.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowRun(false)}>Cancel</button>
            <button className="btn-primary" disabled={runMut.isPending} onClick={() => runMut.mutate()}>
              {runMut.isPending ? 'Running…' : 'Run now'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// CreateTemplateModal — preserved (mostly), minor header polish
function CreateTemplateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schedule, setSchedule] = useState<'monthly' | 'quarterly' | 'annually'>('monthly');
  const [nextRunDate, setNextRunDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [lines, setLines] = useState<Array<any>>([
    { accountCode: '', debit: 0, credit: 0, description: '' },
    { accountCode: '', debit: 0, credit: 0, description: '' },
  ]);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountingApi.listAccounts() });

  const mut = useMutation({
    mutationFn: () => accountingApi.createRecurringTemplate({
      name, description: description || undefined,
      schedule, nextRunDate, endDate: endDate || undefined,
      template: {
        description: templateDescription,
        lines: lines.filter((l) => l.accountCode && ((l.debit ?? 0) > 0 || (l.credit ?? 0) > 0))
                    .map((l) => ({
                      accountCode: l.accountCode,
                      debit: (l.debit ?? 0) > 0 ? l.debit : undefined,
                      credit: (l.credit ?? 0) > 0 ? l.credit : undefined,
                      description: l.description || undefined,
                    })),
      },
    }),
    onSuccess: (d) => { toast.success(`Template "${d.name}" created`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const td = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const tc = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(td - tc) < 0.01 && td > 0;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow text-finance-700 mb-1">Accounting · Recurring</div>
        <h2 className="text-xl font-bold font-display text-slate-900">New recurring JE template</h2>
        <p className="text-sm text-slate-500 mt-1">
          The system will auto-post this JE on the schedule. Pause anytime. Typical uses: monthly rent, depreciation, accruals.
        </p>
      </div>

      <div className="px-6 pb-4 flex-shrink-0 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Monthly rent" />
          </div>
          <div>
            <label className="label">Schedule</label>
            <select className="input" value={schedule} onChange={(e) => setSchedule(e.target.value as any)}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annually">Annually</option>
            </select>
          </div>
          <div>
            <label className="label">Next run</label>
            <input type="date" className="input" value={nextRunDate} onChange={(e) => setNextRunDate(e.target.value)} />
          </div>
          <div>
            <label className="label">End date <span className="font-normal text-slate-400 normal-case tracking-normal">(optional)</span></label>
            <input type="date" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">JE description <span className="font-normal text-slate-400 normal-case tracking-normal">(used on each posted entry)</span></label>
          <input className="input" value={templateDescription} onChange={(e) => setTemplateDescription(e.target.value)} />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-slate-400 normal-case tracking-normal">(internal)</span></label>
          <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-hidden flex flex-col">
        <div className="eyebrow text-slate-500 mb-2">Ledger lines</div>
        <div className="rounded-lg border border-slate-200 overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Account</th>
                <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Memo</th>
                <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Debit</th>
                <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Credit</th>
                <th className="px-2 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map((l, i) => (
                <tr key={i}>
                  <td className="px-2 py-1">
                    <select className="input text-xs !py-1" value={l.accountCode}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, accountCode: e.target.value } : x))}>
                      <option value="">Select…</option>
                      {accountsQuery.data?.map((a: any) => <option key={a.id} value={a.code}>{a.code} {a.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input className="input text-xs !py-1" value={l.description}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" min={0} step={0.01} className="input w-24 text-right text-xs !py-1 tabular-nums" value={l.debit}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, debit: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" min={0} step={0.01} className="input w-24 text-right text-xs !py-1 tabular-nums" value={l.credit}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, credit: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <button className="text-danger text-xs" onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="text-xs font-medium text-finance-700 hover:text-finance-800 mt-2 self-start transition-colors"
          onClick={() => setLines((prev) => [...prev, { accountCode: '', debit: 0, credit: 0, description: '' }])}
        >
          + Add line
        </button>

        <div className={clsx(
          'mt-3 rounded-lg px-3 py-2 text-sm font-medium flex justify-between items-center',
          balanced
            ? 'bg-sage-50 text-sage-800 border border-sage-200'
            : 'bg-danger/10 text-danger border border-danger/30'
        )}>
          <span>
            {balanced ? '✓ Balanced' : `Dr ${fmtCurrency(td)} ≠ Cr ${fmtCurrency(tc)}`}
          </span>
          <span className="font-mono tabular-nums">{fmtCurrency(td)}</span>
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex-shrink-0 flex gap-2 justify-end mt-4">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary"
          disabled={!name || !templateDescription || !balanced || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create template'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default', sublabel,
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
  sublabel?: string;
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-2xs text-slate-500 mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}
